import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useToast } from "@/components/ui/ToastProvider";
import { TeacherLetterDirectory } from "@/components/teacher/TeacherLetterDirectory";
import { TeacherCode } from "@/components/teacher/TeacherCode";
import { addDaysIso, isoMonday, todayLocalISO } from "@/lib/date-utils";
import { extractErrorMessage } from "@/utils/errorHandler";
import { exclusionTitle } from "@/utils/exclusionHatch";
import {
  adminDirectAssignApi,
  type DirectAssignDay,
  type DirectAssignGrid,
  type DirectAssignLesson,
  type DirectAssignSlot,
  type DirectAssignTeacher,
} from "@/services/api/adminDirectAssign";

type CellKey = string;
type Mode = "repeat" | "weeks";

const COLORS = [
  { bg: "bg-indigo-50", rail: "#4f46e5", who: "text-indigo-700" },
  { bg: "bg-teal-50", rail: "#0d9488", who: "text-teal-700" },
  { bg: "bg-orange-50", rail: "#ea580c", who: "text-orange-700" },
  { bg: "bg-fuchsia-50", rail: "#a21caf", who: "text-fuchsia-700" },
] as const;

function hashId(id: string): number {
  let h = 0;
  for (let i = 0; i < id.length; i++) h = (h * 31 + id.charCodeAt(i)) >>> 0;
  return h;
}

function colorFor(id: string): (typeof COLORS)[number] {
  return COLORS[hashId(id) % COLORS.length] as (typeof COLORS)[number];
}

function cellKey(date: string, slotId: string, entryId: string): CellKey {
  return `${date}|${slotId}|${entryId}`;
}

function parseKey(key: CellKey): {
  date: string;
  time_slot_id: string;
  original_entry_id: string;
} {
  const parts = key.split("|");
  return {
    date: parts[0] ?? "",
    time_slot_id: parts[1] ?? "",
    original_entry_id: parts[2] ?? "",
  };
}

function formatDe(iso: string) {
  const parts = iso.split("-");
  return `${parts[2]}.${parts[1]}.${parts[0]}`;
}

function formatDeShort(iso: string) {
  const parts = iso.split("-");
  return `${parts[2]}.${parts[1]}`;
}

function slotLabel(slot: {
  name?: string | null;
  start_time: string;
  end_time: string;
}) {
  const named = slot.name?.trim();
  if (named) return named;
  const start = (slot.start_time || "").slice(0, 5);
  const end = (slot.end_time || "").slice(0, 5);
  return start && end ? `${start}-${end}` : start;
}

function weekFriday(today: string) {
  const fri = addDaysIso(isoMonday(today), 4);
  return fri < today ? today : fri;
}

function monthLabel(year: number, month: number) {
  return new Date(year, month - 1, 1).toLocaleDateString("de-CH", {
    month: "long",
    year: "numeric",
  });
}

function daysInMonth(year: number, month: number) {
  return new Date(year, month, 0).getDate();
}

function qualiRank(q: DirectAssignTeacher["quali"]): number {
  if (q === "all") return 0;
  if (q === "some") return 1;
  if (q === "none") return 2;
  return 3;
}

function compareTeachers(
  a: DirectAssignTeacher,
  b: DirectAssignTeacher
): number {
  const rank = qualiRank(a.quali) - qualiRank(b.quali);
  if (rank) return rank;
  const last = a.last_name.localeCompare(b.last_name, "de-CH");
  if (last) return last;
  return a.first_name.localeCompare(b.first_name, "de-CH");
}

export function AdminStellvertretungPage() {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const today = todayLocalISO();
  const [absentQuery, setAbsentQuery] = useState("");
  const [debouncedAbsent, setDebouncedAbsent] = useState("");
  const [subQuery, setSubQuery] = useState("");
  const [debouncedSub, setDebouncedSub] = useState("");
  const [debouncedSelectedKey, setDebouncedSelectedKey] = useState("");
  const [debouncedPositions, setDebouncedPositions] = useState<
    { date: string; time_slot_id: string; original_entry_id: string }[]
  >([]);
  const [absent, setAbsent] = useState<DirectAssignTeacher | null>(null);
  const [stage, setStage] = useState<"pick" | "work">("pick");
  const [from, setFrom] = useState(today);
  const [to, setTo] = useState(() => weekFriday(today));
  const [cal, setCal] = useState<"from" | "to" | null>(null);
  const [calMonth, setCalMonth] = useState(() => {
    const d = new Date(today + "T00:00:00");
    return { year: d.getFullYear(), month: d.getMonth() + 1 };
  });
  const [mode, setMode] = useState<Mode>("repeat");
  const [openWeeks, setOpenWeeks] = useState<Record<number, boolean>>({});
  const [selected, setSelected] = useState<Set<CellKey>>(new Set());
  const [assigned, setAssigned] = useState<Map<CellKey, string>>(new Map());
  const [picked, setPicked] = useState<string | null>(null);
  const [hints, setHints] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [conflictRows, setConflictRows] = useState<
    { key: CellKey; name: string }[] | null
  >(null);
  const [conflictChoice, setConflictChoice] = useState<
    Record<CellKey, "overwrite" | "skip">
  >({});
  const [openNotice, setOpenNotice] = useState(false);
  const [restOpen, setRestOpen] = useState(false);
  const [knownTeachers, setKnownTeachers] = useState<
    Map<string, DirectAssignTeacher>
  >(new Map());
  const calBoxRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedAbsent(absentQuery), 300);
    return () => clearTimeout(t);
  }, [absentQuery]);
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSub(subQuery), 300);
    return () => clearTimeout(t);
  }, [subQuery]);

  useLayoutEffect(() => {
    if (!cal) return;
    const el = calBoxRef.current;
    const btn = el?.parentElement?.querySelector("button");
    if (!el || !btn) return;
    const place = () => {
      const r = btn.getBoundingClientRect();
      const h = el.offsetHeight;
      const w = el.offsetWidth;
      const gap = 4;
      const below = window.innerHeight - r.bottom - gap;
      const above = r.top - gap;
      const openUp = above >= h || below < h;
      let top = openUp ? r.top - gap - h : r.bottom + gap;
      let left = r.left;
      top = Math.min(Math.max(8, top), Math.max(8, window.innerHeight - 8 - h));
      left = Math.min(
        Math.max(8, left),
        Math.max(8, window.innerWidth - 8 - w)
      );
      el.style.top = `${top}px`;
      el.style.left = `${left}px`;
    };
    place();
    window.addEventListener("resize", place);
    window.addEventListener("scroll", place, true);
    return () => {
      window.removeEventListener("resize", place);
      window.removeEventListener("scroll", place, true);
    };
  }, [cal, calMonth]);

  const absentQ = useQuery({
    queryKey: ["direct-assign-absent", debouncedAbsent],
    queryFn: () =>
      adminDirectAssignApi.listTeachers(debouncedAbsent || undefined),
  });
  const gridQ = useQuery({
    queryKey: ["direct-assign-grid", absent?.id, from, to],
    queryFn: () => adminDirectAssignApi.grid(absent!.id, from, to),
    enabled: !!absent && stage === "work",
  });
  const selectedKey = [...selected].sort().join(",");
  const selectedPositions = useMemo(
    () => [...selected].map((k) => parseKey(k)),
    [selected]
  );
  useEffect(() => {
    const t = setTimeout(() => {
      setDebouncedSelectedKey(selectedKey);
      setDebouncedPositions(selectedPositions);
    }, 300);
    return () => clearTimeout(t);
  }, [selectedKey, selectedPositions]);
  const previewEntryIds = useMemo(() => {
    const ids = new Set<string>();
    for (const p of debouncedPositions) {
      if (p.original_entry_id) ids.add(p.original_entry_id);
    }
    return [...ids];
  }, [debouncedPositions]);
  const candQ = useQuery({
    queryKey: [
      "direct-assign-candidates",
      debouncedSub,
      absent?.id,
      debouncedSelectedKey,
    ],
    queryFn: () =>
      adminDirectAssignApi.candidates({
        exclude_teacher_id: absent!.id,
        q: debouncedSub || undefined,
        positions: debouncedPositions,
      }),
    enabled: !!absent && debouncedPositions.length > 0,
  });
  const previewQ = useQuery({
    queryKey: [
      "direct-assign-lesson-ok",
      absent?.id,
      picked,
      previewEntryIds.join(","),
    ],
    queryFn: () =>
      adminDirectAssignApi.candidates({
        exclude_teacher_id: absent!.id,
        positions: [],
        preview_teacher_id: picked,
        preview_entry_ids: previewEntryIds,
      }),
    enabled: !!absent && !!picked && previewEntryIds.length > 0,
  });

  const absentTeachers = useMemo(
    () => absentQ.data?.teachers ?? [],
    [absentQ.data?.teachers]
  );

  const grid = gridQ.data as DirectAssignGrid | undefined;
  const daysByDate = useMemo(() => {
    const m = new Map<string, DirectAssignDay>();
    for (const d of grid?.days ?? []) m.set(d.date, d);
    return m;
  }, [grid]);
  const slots = useMemo(() => {
    const byId = new Map<string, DirectAssignSlot>();
    for (const s of grid?.slots ?? []) byId.set(s.id, s);
    for (const d of grid?.days ?? []) {
      for (const l of d.lessons) {
        if (!l.time_slot_id || byId.has(l.time_slot_id)) continue;
        byId.set(l.time_slot_id, {
          id: l.time_slot_id,
          slot_number: l.slot_number ?? 0,
          name: l.slot_name,
          start_time: l.start_time || "",
          end_time: l.end_time || "",
        });
      }
    }
    return [...byId.values()].sort((a, b) => {
      const time = a.start_time.localeCompare(b.start_time);
      return time || a.slot_number - b.slot_number;
    });
  }, [grid]);

  useEffect(() => {
    setSelected(new Set());
    setAssigned(new Map());
    setPicked(null);
    setHints([]);
  }, [absent?.id, from, to]);

  const tomorrow = addDaysIso(today, 1);
  const weekEnd = weekFriday(today);
  const retro = from < (grid?.today ?? today);
  const colWeekdays = grid?.saturday ? [0, 1, 2, 3, 4, 5] : [0, 1, 2, 3, 4];
  const dayNames = ["Mo", "Di", "Mi", "Do", "Fr", "Sa"];

  const lessonAt = useCallback(
    (date: string, slotId: string) => {
      const day = daysByDate.get(date);
      return day?.lessons.find((l) => l.time_slot_id === slotId) ?? null;
    },
    [daysByDate]
  );

  const lessonSelectable = (
    lesson:
      | { time_slot_id?: string | null; substitution_excluded?: boolean }
      | null
      | undefined
  ) => !!lesson?.time_slot_id && !lesson.substitution_excluded;

  const toggleCell = (date: string, lesson: DirectAssignLesson) => {
    if (!lessonSelectable(lesson) || !lesson.time_slot_id) return;
    const key = cellKey(date, lesson.time_slot_id, lesson.entry_id);
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const toggleDay = (date: string) => {
    const day = daysByDate.get(date);
    if (!day) return;
    const keys = day.lessons
      .filter(lessonSelectable)
      .map((l) => cellKey(date, l.time_slot_id!, l.entry_id));
    setSelected((prev) => {
      const next = new Set(prev);
      const all =
        keys.length > 0 && keys.every((k) => next.has(k) || assigned.has(k));
      for (const k of keys) {
        if (all) {
          next.delete(k);
        } else next.add(k);
      }
      return next;
    });
  };

  const toggleRepeatDay = (wd: number) => {
    const dates = (grid?.days ?? []).filter(
      (d) => d.weekday === wd && d.lessons.length
    );
    const keys: CellKey[] = [];
    for (const d of dates) {
      for (const l of d.lessons) {
        if (lessonSelectable(l))
          keys.push(cellKey(d.date, l.time_slot_id!, l.entry_id));
      }
    }
    setSelected((prev) => {
      const next = new Set(prev);
      const all =
        keys.length > 0 && keys.every((k) => next.has(k) || assigned.has(k));
      for (const k of keys) {
        if (all) next.delete(k);
        else next.add(k);
      }
      return next;
    });
  };

  const toggleRepeatCell = (wd: number, slotId: string) => {
    const dates = (grid?.days ?? []).filter((d) => d.weekday === wd);
    const keys: CellKey[] = [];
    for (const d of dates) {
      const l = d.lessons.find((x) => x.time_slot_id === slotId);
      if (l && lessonSelectable(l))
        keys.push(cellKey(d.date, slotId, l.entry_id));
    }
    setSelected((prev) => {
      const next = new Set(prev);
      const all =
        keys.length > 0 && keys.every((k) => next.has(k) || assigned.has(k));
      for (const k of keys) {
        if (all) next.delete(k);
        else next.add(k);
      }
      return next;
    });
  };

  const selectionReady =
    selectedKey === debouncedSelectedKey &&
    candQ.isSuccess &&
    !candQ.isFetching;

  const applyAssign = () => {
    if (!picked || selected.size === 0 || !selectionReady) return;
    if (!teachers.some((t) => t.id === picked)) return;
    const t = teacherById(picked);
    if (t) setKnownTeachers((prev) => new Map(prev).set(t.id, t));
    setAssigned((prev) => {
      const next = new Map(prev);
      for (const k of selected) next.set(k, picked);
      return next;
    });
    setSelected(new Set());
  };

  const removeGroup = (teacherId: string) => {
    setAssigned((prev) => {
      const next = new Map(prev);
      const back = new Set(selected);
      for (const [k, id] of prev) {
        if (id === teacherId) {
          next.delete(k);
          back.add(k);
        }
      }
      setSelected(back);
      return next;
    });
  };

  const groups = useMemo(() => {
    const g = new Map<string, CellKey[]>();
    for (const [k, id] of assigned) {
      const arr = g.get(id) ?? [];
      arr.push(k);
      g.set(id, arr);
    }
    return g;
  }, [assigned]);

  const teachers = useMemo(
    () => [...(candQ.data?.teachers ?? [])].sort(compareTeachers),
    [candQ.data?.teachers]
  );
  const lessonOk = previewQ.data?.lesson_ok ?? {};
  useEffect(() => {
    if (!teachers.length) return;
    setKnownTeachers((prev) => {
      const next = new Map(prev);
      for (const t of teachers) next.set(t.id, t);
      return next;
    });
  }, [teachers]);
  useEffect(() => {
    if (!picked || selected.size === 0 || !candQ.isSuccess) return;
    if (selectedKey !== debouncedSelectedKey) return;
    if (!teachers.some((t) => t.id === picked)) setPicked(null);
  }, [
    candQ.isSuccess,
    teachers,
    picked,
    selected.size,
    selectedKey,
    debouncedSelectedKey,
  ]);
  const teacherById = (id: string) =>
    knownTeachers.get(id) ?? teachers.find((t) => t.id === id);
  const pickedTeacher = (picked ? teacherById(picked) : null) ?? null;

  useEffect(() => {
    if (!picked || selected.size === 0) {
      setHints([]);
      return;
    }
    const positions = [...selected].map((k) => {
      const p = parseKey(k);
      return {
        date: p.date,
        time_slot_id: p.time_slot_id,
        original_entry_id: p.original_entry_id,
      };
    });
    let cancelled = false;
    const t = setTimeout(() => {
      adminDirectAssignApi
        .hints(picked, positions)
        .then((r) => {
          if (!cancelled) setHints(r.warnings ?? []);
        })
        .catch(() => {
          if (!cancelled) setHints([]);
        });
    }, 300);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [picked, selected]);

  const bookedKeys = (): { key: CellKey; name: string }[] => {
    const out: { key: CellKey; name: string }[] = [];
    const consider = new Set([...selected, ...assigned.keys()]);
    for (const k of consider) {
      const p = parseKey(k);
      const l = lessonAt(p.date, p.time_slot_id);
      if (l?.occupancy === "booked")
        out.push({ key: k, name: l.booked_teacher_name || "gebucht" });
    }
    return out;
  };

  const openSearchKeys = (): CellKey[] => {
    const consider = new Set([...selected, ...assigned.keys()]);
    const out: CellKey[] = [];
    for (const k of consider) {
      const p = parseKey(k);
      const l = lessonAt(p.date, p.time_slot_id);
      if (l?.occupancy === "open_search") out.push(k);
    }
    return out;
  };

  const leftoverSelected = () => [...selected].filter((k) => !assigned.has(k));
  const leftoverAfterSkip = (
    choice: Record<CellKey, "overwrite" | "skip"> = conflictChoice
  ) => leftoverSelected().filter((k) => choice[k] !== "skip");
  const assignedAfterSkip = (
    choice: Record<CellKey, "overwrite" | "skip"> = conflictChoice
  ) => [...assigned.keys()].filter((k) => choice[k] !== "skip");

  const beginSave = () => {
    if (!absent) return;
    if (selected.size + assigned.size === 0) return;
    const booked = bookedKeys();
    if (booked.length) {
      setConflictRows(booked);
      setConflictChoice({});
      return;
    }
    if (openSearchKeys().length) {
      setOpenNotice(true);
      return;
    }
    if (leftoverSelected().length) {
      setRestOpen(true);
      return;
    }
    void doCommit(null, []);
  };

  const afterConflicts = () => {
    if (!conflictRows) return;
    if (conflictRows.some((r) => !conflictChoice[r.key])) return;
    setConflictRows(null);
    if (openSearchKeys().length) {
      setOpenNotice(true);
      return;
    }
    if (leftoverAfterSkip().length) {
      setRestOpen(true);
      return;
    }
    void doCommit(
      null,
      conflictRows.map((r) => ({
        ...parseKey(r.key),
        action: (conflictChoice[r.key] ?? "skip") as "overwrite" | "skip",
      }))
    );
  };

  const doCommit = async (
    startSearch: boolean | null,
    conflicts: {
      date: string;
      time_slot_id: string;
      original_entry_id: string;
      action: "overwrite" | "skip";
    }[]
  ) => {
    if (!absent) return;
    setSaving(true);
    setError(null);
    const skip = new Set(
      conflicts
        .filter((c) => c.action === "skip")
        .map((c) => cellKey(c.date, c.time_slot_id, c.original_entry_id))
    );
    const positions = [];
    for (const [k, tid] of assigned) {
      if (skip.has(k)) continue;
      const p = parseKey(k);
      positions.push({ ...p, substitute_teacher_id: tid });
    }
    for (const k of selected) {
      if (assigned.has(k) || skip.has(k)) continue;
      positions.push({ ...parseKey(k), substitute_teacher_id: null });
    }
    if (!positions.length) {
      setSaving(false);
      setError(
        "Nichts zu speichern — alle markierten Lektionen wurden ausgelassen."
      );
      return;
    }
    try {
      const result = await adminDirectAssignApi.commit({
        from,
        to,
        absent_teacher_id: absent.id,
        positions,
        start_search: startSearch,
        conflicts,
      });
      showToast({
        message: "Admin-Stellvertretung gespeichert",
        type: "success",
        duration: 4000,
      });
      navigate(`/admin/live-status?detail=${result.request_id}`);
    } catch (e: unknown) {
      const err = e as {
        response?: {
          status?: number;
          data?: { detail?: { code?: string; lessons?: unknown[] } };
        };
      };
      if (
        err?.response?.status === 409 &&
        err.response.data?.detail?.code === "occupancy_conflict"
      ) {
        setError("Belegung hat sich geändert. Bitte Raster neu laden.");
        void gridQ.refetch();
      } else {
        setError(
          extractErrorMessage(e, { loadError: "Speichern fehlgeschlagen" })
        );
      }
    } finally {
      setSaving(false);
    }
  };

  const weeks = useMemo(() => {
    const map = new Map<number, DirectAssignDay[]>();
    for (const d of grid?.days ?? []) {
      const arr = map.get(d.iso_week) ?? [];
      arr.push(d);
      map.set(d.iso_week, arr);
    }
    return [...map.entries()].sort((a, b) =>
      (a[1][0]?.date ?? "").localeCompare(b[1][0]?.date ?? "")
    );
  }, [grid]);

  useEffect(() => {
    const first = weeks[0];
    if (mode === "weeks" && first && Object.keys(openWeeks).length === 0) {
      setOpenWeeks({ [first[0]]: true });
    }
  }, [mode, weeks, openWeeks]);

  const pickCal = (iso: string) => {
    if (cal === "from") {
      setFrom(iso);
      if (to < iso) setTo(iso);
    } else if (cal === "to") {
      setTo(iso);
      if (from > iso) setFrom(iso);
    }
    setCal(null);
  };

  const renderMonth = () => {
    const first = new Date(calMonth.year, calMonth.month - 1, 1);
    const pad = (first.getDay() + 6) % 7;
    const n = daysInMonth(calMonth.year, calMonth.month);
    const cells: (string | null)[] = [...Array(pad).fill(null)];
    for (let i = 1; i <= n; i++) {
      const mm = String(calMonth.month).padStart(2, "0");
      const dd = String(i).padStart(2, "0");
      cells.push(`${calMonth.year}-${mm}-${dd}`);
    }
    return (
      <div
        ref={calBoxRef}
        className="fixed z-20 w-[260px] rounded-xl border bg-white p-2.5 shadow-lg"
      >
        <div className="mb-2 flex items-center justify-between text-sm font-medium">
          <button
            type="button"
            className="px-2"
            onClick={() =>
              setCalMonth((m) =>
                m.month === 1
                  ? { year: m.year - 1, month: 12 }
                  : { year: m.year, month: m.month - 1 }
              )
            }
          >
            ‹
          </button>
          {monthLabel(calMonth.year, calMonth.month)}
          <button
            type="button"
            className="px-2"
            onClick={() =>
              setCalMonth((m) =>
                m.month === 12
                  ? { year: m.year + 1, month: 1 }
                  : { year: m.year, month: m.month + 1 }
              )
            }
          >
            ›
          </button>
        </div>
        <div className="grid grid-cols-7 gap-0.5 text-center text-[11px]">
          {["Mo", "Di", "Mi", "Do", "Fr", "Sa", "So"].map((w) => (
            <div key={w} className="py-1 text-gray-400 font-semibold">
              {w}
            </div>
          ))}
          {cells.map((iso, i) =>
            iso ? (
              <button
                key={iso}
                type="button"
                className={`rounded-md py-1 ${iso === (cal === "from" ? from : to) ? "bg-blue-600 text-white font-bold" : iso >= from && iso <= to ? "bg-blue-100" : "text-gray-700"}`}
                onClick={() => pickCal(iso)}
              >
                {Number(iso.slice(8))}
              </button>
            ) : (
              <div key={`e${i}`} />
            )
          )}
        </div>
      </div>
    );
  };

  const openCal = (which: "from" | "to") => {
    setCal(cal === which ? null : which);
    const iso = which === "from" ? from : to;
    setCalMonth({
      year: Number(iso.slice(0, 4)),
      month: Number(iso.slice(5, 7)),
    });
  };

  const renderDateFields = () => (
    <div className="flex flex-wrap items-end gap-2">
      <div className="relative inline-flex flex-col gap-0.5">
        <span className="text-xs text-gray-500">Anfang</span>
        <button
          type="button"
          className={`min-w-[128px] rounded-lg border bg-white px-2.5 py-1.5 text-left text-[13px] ${cal === "from" ? "border-blue-600 shadow-[0_0_0_3px_#dbeafe]" : "border-gray-200"}`}
          onClick={() => openCal("from")}
        >
          {formatDe(from)}
        </button>
        {cal === "from" ? renderMonth() : null}
      </div>
      <span className="mb-1.5 text-xs text-gray-500">→</span>
      <div className="relative inline-flex flex-col gap-0.5">
        <span className="text-xs text-gray-500">Ende</span>
        <button
          type="button"
          className={`min-w-[128px] rounded-lg border bg-white px-2.5 py-1.5 text-left text-[13px] ${cal === "to" ? "border-blue-600 shadow-[0_0_0_3px_#dbeafe]" : "border-gray-200"}`}
          onClick={() => openCal("to")}
        >
          {formatDe(to)}
        </button>
        {cal === "to" ? renderMonth() : null}
      </div>
    </div>
  );

  const renderPresets = () => {
    const sets = [
      { label: "Heute", a: today, b: today },
      { label: "Morgen", a: tomorrow, b: tomorrow },
      { label: "Diese Woche", a: today, b: weekEnd },
    ];
    return (
      <div className="flex flex-wrap gap-2">
        {sets.map((s) => {
          const on = from === s.a && to === s.b;
          return (
            <button
              key={s.label}
              type="button"
              className={`rounded-full border px-2.5 py-1 text-[11px] ${on ? "border-blue-600 bg-blue-50 font-semibold text-blue-600" : "border-gray-200 bg-white text-gray-700 hover:border-blue-300"}`}
              onClick={() => {
                setFrom(s.a);
                setTo(s.b);
                setCal(null);
              }}
            >
              {s.label}
            </button>
          );
        })}
      </div>
    );
  };

  const cellShadow = (
    tid: string | undefined,
    qok: boolean | undefined
  ): { boxShadow?: string } => {
    const parts: string[] = [];
    if (tid) parts.push(`inset 3px 0 0 ${colorFor(tid).rail}`);
    if (qok === true)
      parts.push(
        "inset 0 0 0 2px #16a34a",
        "inset 0 0 14px rgba(22,163,74,.38)"
      );
    else if (qok === false)
      parts.push(
        "inset 0 0 0 2px #dc2626",
        "inset 0 0 14px rgba(220,38,38,.38)"
      );
    return parts.length ? { boxShadow: parts.join(", ") } : {};
  };

  const cellClass = (
    date: string,
    lesson: DirectAssignLesson | null,
    empty: boolean
  ) => {
    if (!lesson || empty) return "bg-gray-100 text-gray-300 cursor-default";
    const key = cellKey(date, lesson.time_slot_id!, lesson.entry_id);
    if (selected.has(key)) return "bg-blue-50";
    return "bg-white";
  };

  const renderGrid = (dayList: DirectAssignDay[], repeat: boolean) => {
    const cols = colWeekdays;
    return (
      <div
        className="grid gap-px overflow-hidden rounded-lg border border-gray-200 bg-gray-200 text-[11px]"
        style={{ gridTemplateColumns: `5.75rem repeat(${cols.length}, 1fr)` }}
      >
        <div className="bg-gray-50" />
        {cols.map((wd) => {
          const dates = dayList.filter((d) => d.weekday === wd);
          const labelDates = (
            repeat ? (grid?.days ?? []).filter((d) => d.weekday === wd) : dates
          )
            .map((d) => formatDe(d.date))
            .join(" / ");
          const disabled = dates.every((d) => !d.lessons.length);
          const firstDate = dates[0];
          return (
            <button
              key={wd}
              type="button"
              disabled={disabled}
              onClick={() =>
                repeat
                  ? toggleRepeatDay(wd)
                  : firstDate && toggleDay(firstDate.date)
              }
              className="bg-gray-50 px-1 py-1.5 text-center font-semibold text-gray-600 hover:bg-blue-50 disabled:text-gray-300"
            >
              {dayNames[wd]}
              {labelDates ? (
                <div className="font-normal text-[10px] text-gray-400">
                  {labelDates}
                </div>
              ) : null}
            </button>
          );
        })}
        {slots.map((slot) => (
          <div key={slot.id} className="contents">
            <div className="flex items-center justify-center bg-gray-50 px-0.5 text-center text-[10px] leading-tight text-gray-600">
              {slotLabel(slot)}
            </div>
            {cols.map((wd) => {
              const dates = repeat
                ? (grid?.days ?? []).filter((d) => d.weekday === wd)
                : dayList.filter((d) => d.weekday === wd);
              const sample =
                dates.map((d) => lessonAt(d.date, slot.id)).find(Boolean) ??
                null;
              const dateForCell = dates[0]?.date;
              const lesson = sample;
              const excluded = !!lesson?.substitution_excluded;
              const empty = !lesson;
              const displayDate = repeat
                ? (dates.find((d) => lessonAt(d.date, slot.id))?.date ??
                  dateForCell ??
                  "")
                : (dateForCell ?? "");
              const onClick = () => {
                if (!lesson || excluded) return;
                if (repeat) toggleRepeatCell(wd, slot.id);
                else if (dateForCell) toggleCell(dateForCell, lesson);
              };
              let tid: string | undefined;
              if (repeat) {
                for (const d of dates) {
                  const l = lessonAt(d.date, slot.id);
                  if (!l) continue;
                  const found = assigned.get(
                    cellKey(d.date, slot.id, l.entry_id)
                  );
                  if (found) {
                    tid = found;
                    break;
                  }
                }
              } else if (lesson && displayDate) {
                tid = assigned.get(
                  cellKey(displayDate, slot.id, lesson.entry_id)
                );
              }
              const who = tid ? teacherById(tid) : null;
              let occ: DirectAssignLesson["occupancy"] =
                lesson?.occupancy ?? "none";
              if (repeat) {
                const occs = dates
                  .map((d) => lessonAt(d.date, slot.id)?.occupancy)
                  .filter(Boolean);
                if (occs.includes("booked")) occ = "booked";
                else if (occs.includes("open_search")) occ = "open_search";
              }
              const selectedHere =
                !!lesson &&
                (repeat
                  ? dates.some((d) => {
                      const l = lessonAt(d.date, slot.id);
                      return (
                        !!l &&
                        selected.has(cellKey(d.date, slot.id, l.entry_id))
                      );
                    })
                  : !!displayDate &&
                    selected.has(
                      cellKey(displayDate, slot.id, lesson.entry_id)
                    ));
              const qok =
                picked && lesson && selectedHere
                  ? lessonOk[lesson.entry_id]
                  : undefined;
              return (
                <button
                  key={`${slot.id}-${wd}`}
                  type="button"
                  disabled={empty || excluded}
                  onClick={onClick}
                  style={empty ? undefined : cellShadow(tid, qok)}
                  className={`flex min-h-[42px] flex-col justify-center px-1 text-left ${excluded ? "cursor-not-allowed bg-red-50 text-red-800" : tid ? colorFor(tid).bg : cellClass(displayDate, lesson, empty)}`}
                >
                  {lesson ? (
                    <>
                      <span>
                        {lesson.subject_name} {lesson.klass_name}
                      </span>
                      {excluded ? (
                        <span className="text-[10px] text-red-700">
                          {exclusionTitle}
                        </span>
                      ) : null}
                      {who && tid ? (
                        <span
                          className={`text-[10px] font-bold ${colorFor(tid).who}`}
                        >
                          {who.last_name}
                        </span>
                      ) : null}
                      {occ === "booked" ? (
                        <span className="text-[10px] text-orange-700">
                          gebucht
                        </span>
                      ) : null}
                      {occ === "open_search" ? (
                        <span className="text-[10px] text-amber-700">
                          Suche
                        </span>
                      ) : null}
                    </>
                  ) : null}
                </button>
              );
            })}
          </div>
        ))}
      </div>
    );
  };

  const canSave = !!absent && selected.size + assigned.size > 0 && !saving;

  return (
    <div className="p-5">
      {stage === "pick" ? (
        <div className="rounded-xl border border-gray-200 bg-white p-3.5">
          <TeacherLetterDirectory
            title="Verzeichnis"
            items={absentTeachers}
            query={absentQuery}
            onQueryChange={setAbsentQuery}
            selectedId={absent?.id}
            onSelect={setAbsent}
            footer={
              absent ? (
                <div className="mt-3">
                  {renderPresets()}
                  <div className="mt-2">{renderDateFields()}</div>
                  <div className="mt-3.5 flex items-center justify-between gap-2">
                    <div className="text-xs text-gray-500">
                      {absent.last_name}, {absent.first_name} ·{" "}
                      {formatDeShort(from)}–{formatDeShort(to)}
                    </div>
                    <button
                      type="button"
                      className="rounded-lg bg-blue-600 px-3 py-2 text-[13px] font-medium text-white disabled:opacity-45"
                      onClick={() => {
                        setCal(null);
                        setStage("work");
                      }}
                    >
                      Stundenplan öffnen
                    </button>
                  </div>
                </div>
              ) : null
            }
          />
        </div>
      ) : (
        <>
          <div className="mb-2.5 rounded-xl border border-gray-200 bg-white p-3.5">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div className="rounded-lg border border-blue-100 bg-blue-50 px-2.5 py-1.5 text-[12.5px] text-blue-900">
                Im Namen von{" "}
                <strong>
                  {absent?.last_name}, {absent?.first_name}
                </strong>
              </div>
              <button
                type="button"
                className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-[13px] font-medium text-gray-700"
                onClick={() => {
                  setCal(null);
                  setStage("pick");
                }}
              >
                Andere Person
              </button>
            </div>
            <div className="mt-2.5 flex flex-wrap items-end gap-2">
              {renderDateFields()}
              <div className="mb-px flex rounded-lg bg-gray-100 p-[3px]">
                <button
                  type="button"
                  className={`rounded-md px-2.5 py-1.5 text-xs font-medium ${mode === "repeat" ? "bg-white text-blue-600 shadow-sm" : "text-gray-600"}`}
                  onClick={() => setMode("repeat")}
                >
                  Wochen wiederholen
                </button>
                <button
                  type="button"
                  className={`rounded-md px-2.5 py-1.5 text-xs font-medium ${mode === "weeks" ? "bg-white text-blue-600 shadow-sm" : "text-gray-600"}`}
                  onClick={() => setMode("weeks")}
                >
                  einzelne Wochen
                </button>
              </div>
              <span className="mb-1.5 text-xs text-gray-500">
                {selected.size} markiert · {assigned.size} zugeordnet
              </span>
            </div>
            {retro ? (
              <div className="mt-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
                Nachträglich — Zeitraum liegt in der Vergangenheit.
              </div>
            ) : null}
          </div>
          <div className="grid grid-cols-[260px_1fr] gap-2.5">
            <div className="rounded-xl border bg-white p-3.5">
              <div className="text-sm font-semibold">Zuordnungen</div>
              {groups.size === 0 ? (
                <div className="my-2 text-xs text-gray-500">Noch keine</div>
              ) : (
                [...groups.entries()].map(([id, keys]) => {
                  const t = teacherById(id);
                  return (
                    <div
                      key={id}
                      className="flex items-center justify-between border-b py-2 text-sm"
                    >
                      <div>
                        <b>{t ? `${t.last_name}, ${t.first_name}` : id}</b>
                        <div className="text-xs text-gray-500">
                          {keys.length} Lektionen
                        </div>
                      </div>
                      <button
                        type="button"
                        className="text-xs text-red-600"
                        onClick={() => removeGroup(id)}
                      >
                        Entfernen
                      </button>
                    </div>
                  );
                })
              )}
              <div className="mt-2.5 text-sm font-semibold">Lehrer</div>
              <p className="mb-1 text-[11px] text-gray-500">
                Nur ohne Stundenplankollision. Farbe = Quali zu den markierten
                Fächern.
              </p>
              <input
                className="mb-1.5 mt-1 w-full rounded-lg border px-2 py-1.5 text-sm"
                placeholder="Lehrer suchen"
                value={subQuery}
                onChange={(e) => setSubQuery(e.target.value)}
              />
              <div className="flex max-h-[340px] flex-col gap-1.5 overflow-auto">
                {selected.size === 0 ? (
                  <div className="px-1 py-3 text-xs text-gray-400">
                    Lektionen markieren
                  </div>
                ) : selectedKey !== debouncedSelectedKey || candQ.isFetching ? (
                  <div className="px-1 py-3 text-xs text-gray-400">
                    Lehrer laden…
                  </div>
                ) : teachers.length === 0 ? (
                  <div className="px-1 py-3 text-xs text-gray-400">
                    Keine Lehrperson ohne Kollision
                  </div>
                ) : (
                  teachers.map((t) => {
                    const rank = t.quali;
                    const row =
                      rank === "all"
                        ? "bg-emerald-50 border-emerald-300"
                        : rank === "some"
                          ? "bg-orange-50 border-orange-300"
                          : rank === "none"
                            ? "bg-red-50 border-red-300"
                            : "bg-white border-gray-200";
                    const name =
                      rank === "all"
                        ? "text-emerald-800"
                        : rank === "some"
                          ? "text-orange-800"
                          : rank === "none"
                            ? "text-red-800"
                            : "text-gray-800";
                    const label =
                      rank === "all"
                        ? "alle Qualis"
                        : rank === "none"
                          ? "keine Quali"
                          : rank === "some"
                            ? `${t.matched ?? 0}/${t.total ?? 0} Fächer`
                            : "";
                    return (
                      <button
                        key={t.id}
                        type="button"
                        className={`w-full rounded-lg border px-2 py-2 text-left text-sm ${row} ${picked === t.id ? "ring-2 ring-blue-600" : ""}`}
                        onClick={() => setPicked(t.id)}
                      >
                        <div className="flex items-center gap-2">
                          <TeacherCode code={t.teacher_code} />
                          <span className={`font-semibold ${name}`}>
                            {t.last_name}, {t.first_name}
                          </span>
                        </div>
                        {label ? (
                          <div className="text-[11px] text-gray-600">
                            {label}
                          </div>
                        ) : null}
                        {t.subjects?.length ? (
                          <div className="mt-1 flex flex-wrap gap-1">
                            {t.subjects.map((s) => (
                              <span
                                key={s.label}
                                className={`rounded px-1 text-[10px] font-semibold ${s.ok ? "bg-emerald-100 text-emerald-800" : "bg-red-100 text-red-800"}`}
                              >
                                {s.label}
                              </span>
                            ))}
                          </div>
                        ) : null}
                      </button>
                    );
                  })
                )}
              </div>
              {hints.length ? (
                <div className="mt-2 rounded-md border border-orange-200 bg-orange-50 px-2 py-1 text-xs text-orange-800">
                  {hints.join(" · ")}
                </div>
              ) : null}
              <button
                type="button"
                className="mt-2 w-full rounded-lg bg-blue-600 px-3 py-2 text-sm font-medium text-white disabled:opacity-40"
                disabled={
                  !picked ||
                  selected.size === 0 ||
                  !selectionReady ||
                  !teachers.some((t) => t.id === picked)
                }
                onClick={applyAssign}
              >
                {pickedTeacher
                  ? `${pickedTeacher.last_name} zuordnen`
                  : "diesen zuordnen"}
                {selected.size ? ` · ${selected.size}` : ""}
              </button>
              <button
                type="button"
                className="mt-2 w-full rounded-lg border px-3 py-2 text-sm"
                disabled={!canSave}
                onClick={beginSave}
              >
                Speichern
              </button>
              {error ? (
                <div className="mt-2 text-xs text-red-600">{error}</div>
              ) : null}
            </div>
            <div>
              {gridQ.isLoading ? (
                <div className="text-sm text-gray-500">Stundenplan laden…</div>
              ) : null}
              {gridQ.isError ? (
                <div className="mb-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">
                  {extractErrorMessage(gridQ.error, {
                    loadError: "Stundenplan konnte nicht geladen werden",
                  })}
                </div>
              ) : null}
              {grid && grid.days.every((d) => d.lessons.length === 0) ? (
                <div className="mb-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
                  Kein Stundenplan im Zeitraum.
                </div>
              ) : null}
              {grid && mode === "repeat" ? renderGrid(grid.days, true) : null}
              {grid && mode === "weeks"
                ? weeks.map(([kw, days]) => (
                    <div
                      key={kw}
                      className="mb-2 overflow-hidden rounded-[10px] border bg-white"
                    >
                      <button
                        type="button"
                        className="flex w-full justify-between bg-gray-50 px-3.5 py-2.5 text-sm font-semibold"
                        onClick={() =>
                          setOpenWeeks((o) => ({ ...o, [kw]: !o[kw] }))
                        }
                      >
                        KW {kw}
                        <span className="font-normal text-gray-400">
                          {openWeeks[kw] ? "▾" : "▸"}
                        </span>
                      </button>
                      {openWeeks[kw] ? (
                        <div className="p-2">{renderGrid(days, false)}</div>
                      ) : null}
                    </div>
                  ))
                : null}
            </div>
          </div>
        </>
      )}

      {conflictRows && (
        <div className="fixed inset-0 z-30 flex items-center justify-center bg-slate-900/35">
          <div className="w-[480px] max-w-[calc(100%-40px)] rounded-xl bg-white shadow-xl">
            <h3 className="px-4 pt-3.5 text-[15px] font-semibold">
              Bereits gebucht
            </h3>
            <div className="px-4 py-3 text-sm text-gray-700">
              {conflictRows.map((r) => (
                <div
                  key={r.key}
                  className="mb-2 flex items-center justify-between gap-2"
                >
                  <span>
                    {r.key.split("|")[0]} · {r.name}
                  </span>
                  <select
                    className="rounded border px-2 py-1 text-sm"
                    value={conflictChoice[r.key] ?? ""}
                    onChange={(e) =>
                      setConflictChoice((c) => ({
                        ...c,
                        [r.key]: e.target.value as "overwrite" | "skip",
                      }))
                    }
                  >
                    <option value="">wählen</option>
                    <option value="overwrite">überschreiben</option>
                    <option value="skip">auslassen</option>
                  </select>
                </div>
              ))}
            </div>
            <div className="flex justify-end gap-2 border-t px-4 py-2.5">
              <button
                type="button"
                className="rounded-lg border px-3 py-2 text-sm"
                onClick={() => setConflictRows(null)}
              >
                Abbrechen
              </button>
              <button
                type="button"
                className="rounded-lg bg-blue-600 px-3 py-2 text-sm text-white disabled:opacity-40"
                disabled={conflictRows.some((r) => !conflictChoice[r.key])}
                onClick={afterConflicts}
              >
                Weiter
              </button>
            </div>
          </div>
        </div>
      )}
      {openNotice && (
        <div className="fixed inset-0 z-30 flex items-center justify-center bg-slate-900/35">
          <div className="w-[480px] max-w-[calc(100%-40px)] rounded-xl bg-white shadow-xl">
            <h3 className="px-4 pt-3.5 text-[15px] font-semibold">
              Offene Suche
            </h3>
            <p className="px-4 py-3 text-sm text-gray-700">
              Markierte Lektionen mit offener Suche werden ersetzt. Es geht
              keine neue Einladungsmail an bisher kontaktierte Personen.
            </p>
            <div className="flex justify-end gap-2 border-t px-4 py-2.5">
              <button
                type="button"
                className="rounded-lg border px-3 py-2 text-sm"
                onClick={() => setOpenNotice(false)}
              >
                Abbrechen
              </button>
              <button
                type="button"
                className="rounded-lg bg-blue-600 px-3 py-2 text-sm text-white"
                onClick={() => {
                  setOpenNotice(false);
                  if (leftoverAfterSkip().length) setRestOpen(true);
                  else
                    void doCommit(
                      null,
                      Object.entries(conflictChoice).map(([k, action]) => ({
                        ...parseKey(k),
                        action,
                      }))
                    );
                }}
              >
                OK
              </button>
            </div>
          </div>
        </div>
      )}
      {restOpen && (
        <div className="fixed inset-0 z-30 flex items-center justify-center bg-slate-900/35">
          <div className="w-[480px] max-w-[calc(100%-40px)] rounded-xl bg-white shadow-xl">
            <h3 className="px-4 pt-3.5 text-[15px] font-semibold">
              Nicht besetzte Lektionen
            </h3>
            <div className="px-4 py-3 text-sm text-gray-700">
              <p>
                {assignedAfterSkip().length} Lektionen sofort gebucht (keine
                Mail).
              </p>
              <p className="mt-2">
                {leftoverAfterSkip().length} markierte ohne Lehrer.
              </p>
              <p className="mt-2.5">
                <strong>Automatische Suche für den Rest starten?</strong>
              </p>
            </div>
            <div className="flex justify-end gap-2 border-t px-4 py-2.5">
              <button
                type="button"
                className="rounded-lg border px-3 py-2 text-sm"
                onClick={() => {
                  setRestOpen(false);
                  void doCommit(
                    false,
                    Object.entries(conflictChoice).map(([k, action]) => ({
                      ...parseKey(k),
                      action,
                    }))
                  );
                }}
              >
                Nein, offen lassen
              </button>
              <button
                type="button"
                className="rounded-lg bg-blue-600 px-3 py-2 text-sm text-white"
                onClick={() => {
                  setRestOpen(false);
                  void doCommit(
                    true,
                    Object.entries(conflictChoice).map(([k, action]) => ({
                      ...parseKey(k),
                      action,
                    }))
                  );
                }}
              >
                Ja, Suche starten
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
